import BaseAPI from './base-api-client.js';
import { fetchResponseHandler } from '../data/error-utils.js'

/** The api answers 422 when the booking has no currency — treated as "not eligible", never as an error. */
const PAY_LATER_ELIGIBILITY_UNPROCESSABLE_STATUS = 422

/* eslint-disable unicorn/no-null -- mirrors the api payload, which sends null for these fields */
export const INELIGIBLE_PAY_LATER = Object.freeze({
    amount_cents: 0,
    currency: null,
    eligible: false,
    reason: 'unavailable',
    scheduled_charge_at: null,
})
/* eslint-enable unicorn/no-null */

export default class CheckoutAPI extends BaseAPI {

    static async show (bookingId) {
        try {
            const response = await BaseAPI.fetch(`/checkout/${bookingId}`);
            if (!response.ok) {
                throw new Error(`Failed to fetch offer with ID: ${bookingId}.`);
            }
            const data = await response.json();
            return data.data;
        } catch (error) {
            console.error(`Error fetching offer with ID: ${bookingId} -`, error);
            throw error;
        }
    }

    static async create (bookingId, {
        provider,
        paymentMethodId,
        paymentTiming,
    } = {}) {
        try {
            const body = {
                booking_id: bookingId,
                ...(provider && {
                    provider,
                }),
                ...(paymentMethodId && {
                    payment_method_id: paymentMethodId,
                }),
                // TENANT-5655: 'pay_now' | 'pay_later'; omitted → the api defaults to pay_now.
                ...(paymentTiming && {
                    payment_timing: paymentTiming,
                }),
            };
            const response = await BaseAPI.fetchSecure('/checkout', {
                body, method: 'post',
            });
            if (!response.ok) {
                throw new Error('Failed to create booking.');
            }
            const responseData = await response.json();
            return responseData.data;
        } catch (error) {
            console.error('Error creating booking -', error);
            throw error;
        }
    }

    /**
     * Guest "Pay later" eligibility for a booking the guest owns (TENANT-5655).
     *
     * A 422 means the booking has no currency to price the charge in — the guest simply is not
     * offered pay later. Any other failure is thrown so the caller can decide.
     *
     * @param {string} bookingId
     * @returns {Promise<{ eligible: boolean, reason: string|null, scheduled_charge_at: string|null, amount_cents: number, currency: string }>}
     */
    static async getPayLaterEligibility (bookingId) {
        const response = await BaseAPI.fetchSecure(`/booking/${bookingId}/pay-later-eligibility`, {
            method: 'GET',
        });
        if (response.status === PAY_LATER_ELIGIBILITY_UNPROCESSABLE_STATUS) {
            return {
                ...INELIGIBLE_PAY_LATER,
            };
        }
        if (!response.ok) {
            throw new Error(`Failed to read pay-later eligibility for booking ${bookingId}.`);
        }
        const responseData = await response.json();
        return responseData.data;
    }

    static async currencies (code) {
        try {
            const response = await this.fetch(`/checkout/currencies/${code}`, {
                method: 'GET',
            })

            return await fetchResponseHandler(response)
        } catch (error) {
            throw error
        }
    }

}

